import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProjectQuote } from "Api/billingSystem/costs/getProjectQuote.request";

import type {
  ProjectQuote,
  ProjectQuoteParameters
} from "Api/billingSystem/types";
import type { RootState } from "Store/configureStore";

// Thunk
export const getProjectQuoteThunk = createAppAsyncThunk(
  "billingSystem/projectQuote/get",
  async (
    {
      projectId,
      parameters
    }: { projectId: string; parameters?: ProjectQuoteParameters },
    { rejectWithValue }
  ) => {
    try {
      const quote = await getProjectQuote({ projectId, parameters });
      return quote;
    } catch (err) {
      logger(err, {
        action: "get_project_quote",
        projectId
      });
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type — holds the latest quote per project (the parameters vary with
// the plan form's pending values, so only the most recent one is kept).
type ProjectQuoteState = {
  dataByProjectId: Record<
    string,
    {
      quote?: ProjectQuote;
      loading: boolean;
      error?: string;
      requestId?: string;
    }
  >;
};

const initialState: ProjectQuoteState = {
  dataByProjectId: {}
};

// Slice
const projectQuoteSlice = createSlice({
  name: "projectQuote",
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getProjectQuoteThunk.pending, (state, { meta }) => {
        const { projectId } = meta.arg;
        setDeep(state, ["dataByProjectId", projectId, "loading"], true);
        setDeep(state, ["dataByProjectId", projectId, "error"], undefined);
        setDeep(
          state,
          ["dataByProjectId", projectId, "requestId"],
          meta.requestId
        );
      })
      .addCase(getProjectQuoteThunk.fulfilled, (state, { meta, payload }) => {
        const { projectId } = meta.arg;
        // A superseded quote must neither clear the loading flag nor
        // overwrite the latest one.
        if (state.dataByProjectId[projectId]?.requestId !== meta.requestId) {
          return;
        }
        setDeep(state, ["dataByProjectId", projectId, "loading"], false);
        setDeep(state, ["dataByProjectId", projectId, "quote"], payload);
      })
      .addCase(getProjectQuoteThunk.rejected, (state, { meta, payload }) => {
        const { projectId } = meta.arg;
        if (state.dataByProjectId[projectId]?.requestId !== meta.requestId) {
          return;
        }
        setDeep(state, ["dataByProjectId", projectId, "loading"], false);
        setDeep(state, ["dataByProjectId", projectId, "error"], payload?.error);
      });
  }
});

export default projectQuoteSlice.reducer;

// Selectors
const projectQuoteStateSelector = (state: RootState) => state.projectQuote;

export const selectProjectQuote = createSelector(
  projectQuoteStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]?.quote
);

export const selectProjectQuoteLoading = createSelector(
  projectQuoteStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]?.loading ?? false
);

export const selectProjectQuoteError = createSelector(
  projectQuoteStateSelector,
  (_: RootState, projectId: string) => projectId,
  (state, projectId) => state.dataByProjectId[projectId]?.error
);
